import React, { useState } from 'react';
import useSWR from 'swr';
import { Badge, Box, Button, Flex, Heading, Switch, Text, useToast } from '@chakra-ui/react';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';

// Motivos que o backend devolve quando a empresa não pode receber o diagnóstico.
const REASON_LABELS = {
 already_generated: 'Esta empresa já recebeu o diagnóstico do ESG Starter.',
 language_without_template:
  'O idioma desta empresa não tem template de ESG Starter ativo. Configure em ESG Starter.',
 template_not_found: 'O template configurado não existe mais. Revise em ESG Starter.',
 template_without_questions: 'O template configurado está sem perguntas.',
 company_not_found: 'Empresa não encontrada.',
 error: 'Não foi possível enviar agora. Tente novamente.',
};

/**
 * No cadastro é um switch (gera junto com as empresas novas da submissão); na
 * empresa já cadastrada vira envio avulso — é o caminho para quem foi cadastrado
 * antes da feature ou não marcou a opção na hora.
 */
export default function EsgStarterCard({ companyId, value, onChange }) {
 const toast = useToast();
 const [isSending, setIsSending] = useState(false);

 const isExisting = !!companyId;

 const { data: status, mutate } = useSWR(
  isExisting ? `esg-starter/company/${companyId}` : null,
  fetchData
 );

 const enabled = isExisting ? !!status?.hasStarter : !!value;

 async function sendStarter() {
  setIsSending(true);
  try {
   const { data } = await baseService.post(`esg-starter/company/${companyId}`);
   if (data?.created) {
    toast({ title: 'ESG Starter enviado para a empresa', status: 'success' });
   } else {
    toast({
     title: 'Nada foi enviado',
     description: REASON_LABELS[data?.reason] || REASON_LABELS.error,
     status: 'warning',
    });
   }
   await mutate();
  } catch {
   toast({ title: REASON_LABELS.error, status: 'error' });
  } finally {
   setIsSending(false);
  }
 }

 return (
  // Mesmo padrão visual dos cards de idioma da tela de configuração do ESG
  // Starter (pages/esg-starter): faixa esquerda + badge mudam com o estado.
  <Box
   mt='24px'
   bg='white'
   borderRadius='12px'
   border='1px solid #E5E5E5'
   borderLeft={`4px solid ${enabled ? '#0073BB' : '#D7DBE0'}`}
   p='20px'
   transition='0.15s'
  >
   <Flex justify='space-between' align='center' gap='12px'>
    <Flex align='center' gap='10px' wrap='wrap'>
     <Heading fontSize='16px'>ESG Starter</Heading>
     <Badge
      borderRadius='full'
      px='8px'
      py='1px'
      fontSize='10px'
      textTransform='none'
      color={enabled ? '#1F7A33' : '#5C6066'}
      bg={enabled ? '#E3F5E8' : '#EDEFF2'}
     >
      {isExisting ? (enabled ? 'Enviado' : 'Não enviado') : enabled ? 'Ativo' : 'Inativo'}
     </Badge>
    </Flex>

    {isExisting ? (
     <Button
      variant='primary'
      h='42px'
      minW='180px'
      isLoading={isSending}
      isDisabled={!status || !status.canGenerate}
      onClick={sendStarter}
     >
      Enviar ESG Starter
     </Button>
    ) : (
     <Switch
      colorScheme='blue'
      isChecked={enabled}
      onChange={(e) => onChange(e.target.checked)}
     />
    )}
   </Flex>

   <Text fontSize='sm' color='#5C6066' mt='8px'>
    {isExisting
     ? status?.hasStarter
       ? REASON_LABELS.already_generated
       : status?.reason
         ? REASON_LABELS[status.reason]
         : 'Envia o diagnóstico padrão do ESG Starter para esta empresa, use para empresas cadastradas que não receberam na hora do cadastro.'
     : 'Gera automaticamente o diagnóstico padrão do ESG Starter para as empresas novas desta submissão, igual ao que acontece no auto-cadastro.'}
   </Text>
  </Box>
 );
}
